/**
 * Hooks for Knowledge Tag Taxonomy API
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api-client";
import type { KnowledgeTagGroup } from "@/types/api/knowledge-tags";

const KNOWLEDGE_TAGS_QUERY_KEYS = {
    all: ["knowledge-tags"] as const,
    grouped: () => [...KNOWLEDGE_TAGS_QUERY_KEYS.all, "grouped"] as const,
    groups: () => [...KNOWLEDGE_TAGS_QUERY_KEYS.all, "groups"] as const,
};

/**
 * Fetch all tags grouped by group
 */
export function useKnowledgeTags() {
    return useQuery({
        queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.grouped(),
        queryFn: async (): Promise<KnowledgeTagGroup[]> => {
            const response = await apiGet<{ success: boolean; data: KnowledgeTagGroup[] }>("/knowledge-tags");
            return response.data || [];
        },
    });
}

/**
 * Fetch tag groups only
 */
export function useKnowledgeTagGroups() {
    return useQuery({
        queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.groups(),
        queryFn: async (): Promise<KnowledgeTagGroup[]> => {
            const response = await apiGet<{ success: boolean; data: KnowledgeTagGroup[] }>("/knowledge-tags/groups");
            return response.data || [];
        },
    });
}

/**
 * Assign tags to a document
 */
export function useAssignKnowledgeTags() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            documentId,
            tagIds,
            assignedBy = "admin",
        }: {
            documentId: string;
            tagIds: string[];
            assignedBy?: string;
        }) => {
            return await apiPost(`/knowledge-tags/assign/${documentId}`, {
                tag_ids: tagIds,
                assigned_by: assignedBy,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all });
        },
    });
}

/**
 * Create a new tag
 */
export function useCreateKnowledgeTag() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: { group_id: string; slug: string; label: string; description?: string }) => {
            const response = await apiPost<{ success: boolean; data: unknown }>("/knowledge-tags", data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all });
        },
    });
}

/**
 * Create a new tag group
 */
export function useCreateKnowledgeTagGroup() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: { name: string; label: string; description?: string; color?: string }) => {
            const response = await apiPost<{ success: boolean; data: unknown }>("/knowledge-tags/groups", data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all });
        },
    });
}

export interface TagStats {
    entity_summary: { entity_type: string; assignments: number; entities: number }[];
    group_breakdown: { name: string; label: string; color: string; by_entity: Record<string, number> }[];
    top_tags: { slug: string; label: string; group: string; color: string; by_entity: Record<string, number>; total: number }[];
}

export function useKnowledgeTagStats() {
    return useQuery({
        queryKey: [...KNOWLEDGE_TAGS_QUERY_KEYS.all, "stats"] as const,
        queryFn: async (): Promise<TagStats> => {
            const response = await apiGet<{ success: boolean; data: TagStats }>("/knowledge-tags/stats");
            return response.data;
        },
    });
}

export function useAutoTagDocument() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (docId: string): Promise<{ tags_assigned: string[]; tags_already_present: string[]; total_new: number }> => {
            const response = await apiPost<{ success: boolean; data: { tags_assigned: string[]; tags_already_present: string[]; total_new: number } }>(
                `/knowledge-tags/auto-tag/${docId}`,
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all });
            queryClient.invalidateQueries({ queryKey: ["knowledge-bases"] });
        },
    });
}

export function useUpdateKnowledgeTag() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tagId, data }: { tagId: string; data: { label?: string; description?: string; is_active?: boolean } }) => {
            const response = await apiPut<{ success: boolean; data: unknown }>(`/knowledge-tags/${tagId}`, data);
            return response.data;
        },
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all }); },
    });
}

export function useUpdateKnowledgeTagGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ groupId, data }: { groupId: string; data: { label?: string; description?: string; color?: string; sort_order?: number } }) => {
            const response = await apiPut<{ success: boolean; data: unknown }>(`/knowledge-tags/groups/${groupId}`, data);
            return response.data;
        },
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all }); },
    });
}

export function useDeleteKnowledgeTag() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (tagId: string) => { return await apiDelete(`/knowledge-tags/${tagId}`); },
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all }); },
    });
}

export function useDeleteKnowledgeTagGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (groupId: string) => { return await apiDelete(`/knowledge-tags/groups/${groupId}`); },
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: KNOWLEDGE_TAGS_QUERY_KEYS.all }); },
    });
}
